import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, History, Pencil } from 'lucide-react'
import type {
  ProfileFieldDef,
  PropertyProfileCompleteness,
  PropertyProfileSectionId,
  PropertyProfileV1,
} from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { apiErrorMessage } from '@/lib/api-error'
import { formatLocaleDateTime } from '@/lib/format-utils'
import {
  PROFILE_SECTIONS,
  bare,
  PROFILE_STATUS_LABELS,
  SECTION_STATUS_LABELS,
  SOURCE_LABELS,
  fieldApplies,
  formatAnswer,
  groupItems,
  isLeaf,
  missingInSection,
  profileSectionFields,
  listingDiffRows,
  storedSection,
  type ListingDiffRow,
  type StoredFields,
} from '@/lib/profile'
import { ProfileSectionSheet } from './profile-section-sheet'
import { ProfileRevisionsSheet } from './profile-revisions-sheet'
import { LinkUplistingDialog, type LinkTarget } from '@/components/onboarding/link-uplisting-dialog'
import { ProfileDocumentsCard, SectionDocumentSlots } from './profile-documents'
import { usePropertyDocuments } from '@/hooks/useApi'

export function SourceBadge({ source }: { source: keyof typeof SOURCE_LABELS }) {
  return (
    <Badge
      variant={source === 'ops' ? 'default' : 'secondary'}
      className="shrink-0 px-1.5 py-0 text-[10px] font-medium"
      title={`Answered by ${SOURCE_LABELS[source].toLowerCase()}`}
    >
      {SOURCE_LABELS[source]}
    </Badge>
  )
}

/** A door code or wifi password: hidden until asked for, so it is not on screen by default. */
function SensitiveValue({ text }: { text: string }) {
  const [shown, setShown] = useState(false)
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono">{shown ? text : '••••••'}</span>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs text-mint hover:underline"
        onClick={() => setShown(v => !v)}
        aria-label={shown ? 'Hide value' : 'Show value'}
      >
        {shown ? (
          <EyeOff className="h-3 w-3" aria-hidden />
        ) : (
          <Eye className="h-3 w-3" aria-hidden />
        )}
        {shown ? 'Hide' : 'Show'}
      </button>
    </span>
  )
}

interface FieldsViewProps {
  fields: readonly ProfileFieldDef[]
  stored: StoredFields
  missing: Set<string>
  /** Path prefix for required-missing lookups inside groups: `<group>.<itemId>.` */
  prefix: string
  sectionFieldsOf: (sectionId: string) => Record<string, unknown> | undefined
}

/** The answers of a section or group item, read-only: answered first, then what is still open. */
function FieldsView({ fields, stored, missing, prefix, sectionFieldsOf }: FieldsViewProps) {
  const [showOpen, setShowOpen] = useState(false)
  const applicable = fields.filter(field => fieldApplies(field, stored, sectionFieldsOf))
  // A leaf whose value is null (an answer cleared) is unanswered too.
  const isAnswered = (key: string) => {
    const value = bare(stored[key])
    return value !== null && value !== undefined
  }
  const answered = applicable.filter(field => isAnswered(field.key))
  const open = applicable.filter(field => !isAnswered(field.key))
  const requiredOpen = open.filter(field => missing.has(`${prefix}${field.key}`))

  return (
    <div className="space-y-3">
      {answered.length > 0 && (
        <dl className="divide-y divide-border">
          {answered.map(field => {
            const value = stored[field.key]
            if (field.kind === 'group') {
              const items = groupItems(value) ?? []
              return (
                <div key={field.key} className="space-y-2 py-2">
                  <dt className="text-sm text-textMuted">{field.label}</dt>
                  <dd>
                    {items.length === 0 ? (
                      <span className="text-sm text-text">None</span>
                    ) : (
                      <ol className="space-y-2">
                        {items.map((item, index) => (
                          <li key={item.id} className="rounded-card border border-border p-3">
                            <div className="mb-1 text-sm font-medium text-text">
                              {field.item_label} {index + 1}
                            </div>
                            <FieldsView
                              fields={field.fields}
                              stored={item}
                              missing={missing}
                              prefix={`${prefix}${field.key}.${item.id}.`}
                              sectionFieldsOf={sectionFieldsOf}
                            />
                          </li>
                        ))}
                      </ol>
                    )}
                  </dd>
                </div>
              )
            }
            if (!isLeaf(value)) return null
            const text = formatAnswer(field, value.value)
            return (
              <div
                key={field.key}
                className="grid gap-1 py-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4"
              >
                <dt className="text-sm text-textMuted">{field.label}</dt>
                <dd className="flex min-w-0 items-start justify-between gap-2 text-sm text-text">
                  <span className="min-w-0 whitespace-pre-wrap wrap-break-word">
                    {field.sensitive ? <SensitiveValue text={text} /> : text}
                  </span>
                  <SourceBadge source={value.source} />
                </dd>
              </div>
            )
          })}
        </dl>
      )}

      {requiredOpen.length > 0 && (
        <p className="text-xs text-amber">
          Required, not answered: {requiredOpen.map(field => field.label).join(', ')}
        </p>
      )}

      {open.length > 0 && (
        <div className="space-y-1">
          <button
            type="button"
            className="text-xs text-mint hover:underline"
            onClick={() => setShowOpen(v => !v)}
            aria-expanded={showOpen}
          >
            {showOpen ? 'Hide' : 'Show'} {open.length} unanswered question
            {open.length === 1 ? '' : 's'}
          </button>
          {showOpen && (
            <ul className="space-y-0.5 text-xs text-textMuted">
              {open.map(field => (
                <li key={field.key}>{field.label}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function ListingDiff({ rows }: { rows: ListingDiffRow[] }) {
  return (
    <Card data-testid="profile-listing-diff">
      <CardHeader>
        <CardTitle>Uplisting disagrees</CardTitle>
        <CardDescription>
          The listing says something else than the owner&apos;s answers. Uplisting is what guests
          see, so fix whichever is wrong.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border text-sm">
          {rows.map(row => (
            <div key={row.field} className="grid gap-1 py-2 sm:grid-cols-3 sm:gap-4">
              <dt className="font-medium text-text">{row.label}</dt>
              <dd className="wrap-break-word text-textMuted">
                <span className="text-xs">Owner says </span>
                <span className="text-text">{row.profile}</span>
              </dd>
              <dd className="wrap-break-word text-textMuted">
                <span className="text-xs">Uplisting says </span>
                <span className="text-text">{row.uplisting}</span>
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

interface SummaryProps {
  profile: PropertyProfileV1
  completeness: PropertyProfileCompleteness
  propertyId: string
}

function ProfileSummary({ profile, completeness, propertyId }: SummaryProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [reviewing, setReviewing] = useState(false)
  const changed = PROFILE_SECTIONS.filter(
    def => storedSection(profile, def.id).changed_since_review
  )
  // admin-api refuses a review while the profile is a draft (409).
  const canReview = profile.status !== 'draft'
  const needsReview = profile.status === 'submitted' || changed.length > 0

  const markReviewed = async () => {
    setReviewing(true)
    try {
      const result = await api.reviewPropertyProfile(propertyId)
      // The answer is the whole profile; merged so `listing_diff` stays.
      queryClient.setQueryData(['property-profile', propertyId], (old: unknown) =>
        old && typeof old === 'object' ? { ...old, ...result } : result
      )
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      toast({ title: 'Profile marked reviewed' })
    } catch (err) {
      toast({
        title: 'Could not mark the profile reviewed',
        description: apiErrorMessage(err, 'Please try again.'),
        variant: 'destructive',
      })
    } finally {
      setReviewing(false)
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-4">
        <div className="space-y-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                profile.status === 'reviewed'
                  ? 'success'
                  : profile.status === 'submitted'
                    ? 'warning'
                    : 'secondary'
              }
            >
              {PROFILE_STATUS_LABELS[profile.status]}
            </Badge>
            <span className="text-text">
              {completeness.sections_complete} of {completeness.sections_total} sections complete
            </span>
          </div>
          <p className="text-textMuted">
            {profile.submitted_at
              ? `Submitted ${formatLocaleDateTime(profile.submitted_at)}.`
              : 'Not submitted by the owner yet.'}
            {profile.reviewed_at &&
              ` Reviewed ${formatLocaleDateTime(profile.reviewed_at)}${profile.reviewed_by ? ` by ${profile.reviewed_by}` : ''}.`}
          </p>
          {changed.length > 0 && (
            <p className="text-amber">
              Changed since review: {changed.map(def => def.title).join(', ')}
            </p>
          )}
        </div>
        {canReview ? (
          needsReview && (
            <Button onClick={() => void markReviewed()} disabled={reviewing}>
              {reviewing ? 'Saving...' : 'Mark reviewed'}
            </Button>
          )
        ) : (
          <p className="max-w-xs text-xs text-textMuted">
            You can mark it reviewed once the owner submits it.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The Profile tab (CAP-1467): the owner's property profile in read form, all
 * twelve sections, with whose answer each value is, what changed since the
 * last review, ops corrections ("Correct", saved as `source: ops`) and each
 * section's history. Every property has one: before the first save it is the
 * empty profile admin-api returns.
 */
export function ProfileTab({
  propertyId,
  isActive,
  linkable,
}: {
  propertyId: string
  isActive: boolean
  /** Set for a property still onboarding: offers "Link to Uplisting listing". */
  linkable?: LinkTarget
}) {
  const [link, setLink] = useState<LinkTarget | null>(null)
  const [editing, setEditing] = useState<PropertyProfileSectionId | null>(null)
  const [history, setHistory] = useState<PropertyProfileSectionId | null>(null)
  const documents = usePropertyDocuments(propertyId, isActive)
  const query = useQuery({
    queryKey: ['property-profile', propertyId],
    queryFn: () => api.getPropertyProfile(propertyId),
    enabled: !!propertyId && isActive,
    staleTime: 30000,
  })

  if (query.isLoading) {
    return (
      <div className="space-y-4" aria-label="Loading profile">
        <LoadingSkeleton className="h-20 w-full" />
        <LoadingSkeleton className="h-48 w-full" />
        <LoadingSkeleton className="h-48 w-full" />
      </div>
    )
  }
  if (query.isError || !query.data) {
    // The documents do not depend on the profile, so they stay reachable.
    return (
      <div className="space-y-4">
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
        <ProfileDocumentsCard propertyId={propertyId} isActive={isActive} />
      </div>
    )
  }

  const { profile, completeness } = query.data
  const listingDiff = listingDiffRows(query.data.listing_diff)
  const sectionFieldsOf = profileSectionFields(profile)

  return (
    <div className="space-y-4">
      <ProfileSummary profile={profile} completeness={completeness} propertyId={propertyId} />
      {linkable && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <p className="text-sm text-textMuted">
              Still onboarding. Once the listing exists in Uplisting and has synced, link this
              property to it.
            </p>
            <Button variant="outline" onClick={() => setLink(linkable)}>
              Link to Uplisting listing
            </Button>
          </CardContent>
        </Card>
      )}
      {listingDiff.length > 0 && <ListingDiff rows={listingDiff} />}

      {PROFILE_SECTIONS.map(def => {
        const section = storedSection(profile, def.id)
        return (
          <Card key={def.id} data-testid={`profile-section-${def.id}`}>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
              <div className="min-w-0 space-y-1">
                <CardTitle className="flex flex-wrap items-center gap-2">
                  <span>
                    {def.order}. {def.title}
                  </span>
                  <Badge variant={section.status === 'complete' ? 'success' : 'secondary'}>
                    {SECTION_STATUS_LABELS[section.status]}
                  </Badge>
                  {section.changed_since_review && (
                    <Badge variant="warning">Changed since review</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {section.updated_at
                    ? `Last saved ${formatLocaleDateTime(section.updated_at)}`
                    : def.why_we_ask}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setHistory(def.id)}
                  aria-label={`History of ${def.title}`}
                >
                  <History className="mr-1 h-4 w-4" aria-hidden />
                  History
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(def.id)}
                  aria-label={`Correct ${def.title}`}
                >
                  <Pencil className="mr-1 h-4 w-4" aria-hidden />
                  Correct
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <FieldsView
                fields={def.fields}
                stored={section.fields}
                missing={missingInSection(completeness.required_missing, def.id)}
                prefix=""
                sectionFieldsOf={sectionFieldsOf}
              />
              <SectionDocumentSlots
                propertyId={propertyId}
                slots={def.document_slots}
                sectionId={def.id}
                documents={documents.data?.documents}
                failed={documents.isError}
                applies={slot => fieldApplies(slot, section.fields, sectionFieldsOf)}
              />
            </CardContent>
          </Card>
        )
      })}

      <ProfileDocumentsCard propertyId={propertyId} isActive={isActive} />
      <LinkUplistingDialog target={link} onClose={() => setLink(null)} />

      <ProfileSectionSheet
        propertyId={propertyId}
        profile={profile}
        sectionId={editing}
        onClose={() => setEditing(null)}
      />
      <ProfileRevisionsSheet
        propertyId={propertyId}
        sectionId={history}
        onClose={() => setHistory(null)}
      />
    </div>
  )
}
